/* eslint-disable no-restricted-globals */
/* eslint react-hooks/exhaustive-deps: 0 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { withTranslation } from "react-i18next";
import { useParams, Link, useHistory } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
  Container,
  Row,
  Col,
  FormGroup,
  Card,
  CardBody,
  Spinner,
  Button,
  Label,
} from "reactstrap";
import Select from "react-select";
import Switch from "react-switch";
import { useDispatch, useSelector } from "react-redux";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import schema from "./AdditionalFieldValidation.js";

import {
  getAdditionalField,
  resetAdditionalFields,
  createAdditionalField,
  editAdditionalField,
  resetError
} from "../../store/additionalFields/actions";

import Breadcrumbs from "../../components/Common/Breadcrumb";
import { pick } from "lodash";
import { AdditionalFieldSelectConfiguration } from "./AdditionalFieldSelectConfiguration.js";
import { additionalFieldTypes } from "./additionalFieldTypes.js";
import { AdditionalFieldPreview } from "./AdditionalFieldPreview.js";

const AdditionalField = ({ t }) => {
  const { idAdditionalField } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const [errors, setErrors] = useState({});

  const {
    dataAdditionalFieldsGetSet,
    loadingAdditionalField,
    successAdditionalFields,
    dataAdditionalFieldsEditCreate,
    errorAdditionalFields,
  } = useSelector((state) => state.AdditionalFields);

  const [additionalFieldValues, setAdditionalFieldValues] = useState({
    name: "",
    required: false,
    skills: [],
    type: "",
    configuration: {},
    placeholder: "",
  });

  const showToast = useCallback(
    (type, message) => {
      toastr[type](message);
    },
    [successAdditionalFields]
  );
  toastr.options = {
    closeButton: false,
    debug: false,
    newestOnTop: false,
    progressBar: true,
    positionClass: "toast-top-right",
    preventDuplicates: true,
    onclick: null,
    showDuration: "200",
    hideDuration: "200",
    timeOut: "2000",
    extendedTimeOut: "2000",
    showEasing: "swing",
    hideEasing: "linear",
    showMethod: "fadeIn",
    hideMethod: "fadeOut",
    onHidden: () =>
      successAdditionalFields
      && dispatch(resetError())
  };

  const { register, handleSubmit } = useForm();

  const handleCreateEditScript = async () => {
    try {
      await schema.validate(additionalFieldValues, { abortEarly: false });
      if (idAdditionalField) {
        dispatch(editAdditionalField(idAdditionalField, additionalFieldValues));
      } else {
        dispatch(createAdditionalField(additionalFieldValues));
      }
    } catch (err) {
      const validationErrors = {};
      err.inner.forEach((error) => {
        if (error.path != null) {
          validationErrors[error.path] = error.message;
        }
      });
      setErrors(validationErrors)
    }
  };

  const setClassName = (errors, classname) => {
    return errors ? "av-invalid is-invalid " + classname : classname;
  };

  const skills = useMemo(() => dataAdditionalFieldsGetSet?.skills ?? [], [dataAdditionalFieldsGetSet?.skills])

  useEffect(() => {
    resetAdditionalFields();

    if (idAdditionalField) {
      dispatch(getAdditionalField(idAdditionalField, true));
      return;
    }

    dispatch(getAdditionalField(null, true))
  }, []);

  useEffect(() => {
    if (!idAdditionalField) return
    if (!dataAdditionalFieldsGetSet?.data) return
    setAdditionalFieldValues(() => ({
      ...pick(dataAdditionalFieldsGetSet.data, 'name', 'required', 'type', 'configuration', 'placeholder', 'key'),
      skills: dataAdditionalFieldsGetSet.data.additionalFieldsSkills?.map((e) => e.skillId),
    }))
  }, [idAdditionalField, dataAdditionalFieldsGetSet]);

  useEffect(() => {
    if (successAdditionalFields) {
      if (dataAdditionalFieldsEditCreate?.message) {
        showToast("success", t(dataAdditionalFieldsEditCreate.message));
        dispatch(resetAdditionalFields());
        history.push("/additional-fields")
      }
    }
  }, [successAdditionalFields]);

  useEffect(() => {
    if (errorAdditionalFields && typeof errorAdditionalFields === "object") {
      errorAdditionalFields.map((error) => showToast("error", t(error.value)));
      dispatch(resetError());
    } else {
      if (errorAdditionalFields) {
        showToast("error", t(errorAdditionalFields));
        dispatch(resetError());
      }
    }
  }, [errorAdditionalFields]);

  const handleChangeKeyValue = useCallback((event) => {
    event.persist()

    setAdditionalFieldValues(additionalFieldValues => ({
      ...additionalFieldValues,
      key: event.target.value,
    }));
  }, [])

  const handleChangeConfiguration = useCallback((configuration) => {
    setAdditionalFieldValues(value => ({ ...value, configuration }))
  }, [])

  if (loadingAdditionalField)
    return (
      <div className="d-flex align-items-center justify-content-center vh-100">
        <Spinner size="sm" animation="border" style={{ marginTop: 40, height: 50, width: 50 }} />
      </div>
    )

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            mainPath="/dashboard"
            path="/additional-fields"
            title={t("Dashboard")}
            breadcrumbItem={
              idAdditionalField
                ? t("Edit Additional Field")
                : t("Add Additional Field")
            }
          />
          <Row>
            <Col xs="12" className="customContent">
              <Card>
                <CardBody>
                  <form onSubmit={handleSubmit(handleCreateEditScript)}>
                    <Row className="mb-3 justify-content-between">
                      <Col className="pr-2" lg="6">
                        <Row>
                          <Col md="12">
                            <FormGroup>
                              <Label>
                                {t("Field name") + " *"}
                              </Label>
                              <input
                                type="text"
                                name="additionalFieldName"
                                ref={register}
                                className={setClassName(
                                  errors.name,
                                  "form-control"
                                )}
                                placeholder={t("Insert field name")}
                                onChange={(e) => {
                                  setAdditionalFieldValues({
                                    ...additionalFieldValues,
                                    name: e.target.value,
                                  });
                                }}
                                value={additionalFieldValues.name}
                              />
                              <div className="invalid-feedback d-block">
                                {t(errors.name)}
                              </div>
                            </FormGroup>
                          </Col>
                          
                        </Row>
                        <Row>
                          <Col md="12">
                            <FormGroup className="select2-container">
                              <Label>
                                {t("Allow Formats") + " *"}
                              </Label>
                              <Select
                                id="type"
                                name="type"
                                ref={() => register({ name: "type" })}
                                classNamePrefix="select2-selection"
                                className={setClassName(errors.type, "select2-selection" )}
                                getOptionLabel={(option) => t(option.label)}
                                getOptionValue={(option) => option.type}
                                placeholder={t("Choose") + "..."}setAdditionalFieldValues
                                options={additionalFieldTypes}
                                onChange={(option) => {
                                  setAdditionalFieldValues(value => ({
                                    ...value,
                                    type: option.type,
                                    configuration: typeof option.configuration === 'function'
                                      ? option.configuration()
                                      : option.configuration ?? {},
                                  }));
                                }}
                                value={additionalFieldTypes.find((option) => option.type === additionalFieldValues.type)}
                              />
                              <div className="invalid-feedback d-block">
                                {t(errors.type)}
                              </div>
                            </FormGroup>
                          </Col>
                        </Row>
                        {
                          additionalFieldValues.type === "select" && (
                            <AdditionalFieldSelectConfiguration
                              configuration={additionalFieldValues.configuration}
                              errors={errors}
                              onChange={handleChangeConfiguration}
                            />
                          )
                        }
                        {additionalFieldValues.type === "text" && (
                          <Row>
                            <Col md="6">
                              <FormGroup>
                                <Label>
                                  {t("Minimum characters") + " *"}
                                </Label>
                                <input
                                  type="number"
                                  name="additionalFieldMinLength"
                                  ref={register}
                                  className={setClassName(
                                    errors['configuration.minLength'],
                                    "form-control"
                                  )}
                                  placeholder={0}
                                  onChange={(e) => {
                                    setAdditionalFieldValues({
                                      ...additionalFieldValues,
                                      configuration: {
                                        ...additionalFieldValues.configuration,
                                        minLength: e.target.value,
                                      }
                                    });
                                  }}
                                  value={additionalFieldValues.configuration.minLength}
                                />
                                <div className="d-block">
                                  <small>
                                    {t("Enter only positive numbers")}
                                  </small>
                                </div>
                                <div className="invalid-feedback d-block">
                                  {t(errors['configuration.minLength'])}
                                </div>
                              </FormGroup>
                            </Col>
                            <Col md="6">
                              <FormGroup>
                                <Label>
                                  {t("Maximum characters") + " *"}
                                </Label>
                                <input
                                  type="number"
                                  name="additionalFieldMaxLength"
                                  ref={register}
                                  className={setClassName(
                                    errors['configuration.maxLength'],
                                    "form-control"
                                  )}
                                  placeholder={200}
                                  onChange={(e) => {
                                    setAdditionalFieldValues({
                                      ...additionalFieldValues,
                                      configuration: {
                                        ...additionalFieldValues.configuration,
                                        maxLength: e.target.value,
                                      }
                                    });
                                  }}
                                  value={additionalFieldValues.configuration.maxLength}
                                />
                                <div className="d-block">
                                  <small>
                                    {t("Limit 200") +
                                      ". " +
                                      t("Enter only positive numbers")}
                                  </small>
                                </div>
                                <div className="invalid-feedback d-block">
                                  {t(errors['configuration.maxLength'])}
                                </div>
                              </FormGroup>
                            </Col>
                          </Row>
                        )}
                        <Row>
                          <Col md="12">
                            <FormGroup>
                              <Label>
                                {t("Help text")}
                              </Label>
                              <input
                                type="text"
                                name="placeholder"
                                ref={register}
                                className={setClassName(
                                  errors.placeholder,
                                  "form-control"
                                )}
                                placeholder={t("Enter the text")}
                                onChange={(e) => {
                                  setAdditionalFieldValues({
                                    ...additionalFieldValues,
                                    placeholder: e.target.value,
                                  });
                                }}
                                value={additionalFieldValues.placeholder}
                              />
                              <div className="invalid-feedback d-block">
                                {t(errors.placeholder)}
                              </div>
                              <div className="d-block">
                                {additionalFieldValues.placeholder && <small>{`(${additionalFieldValues.placeholder.length}/200)`}</small>}
                              </div>
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col md="12">
                            <FormGroup className="d-flex justify-content-between">
                              <Label>
                                {t("Turn required field")}
                              </Label>
                              <Switch
                                className="d-flex align-self-center"
                                onColor="#34c38f"
                                offColor="#bfc8e2"
                                onHandleColor="#222736"
                                offHandleColor="#32394E"
                                checked={additionalFieldValues.required}
                                onChange={(e) => {
                                  setAdditionalFieldValues({
                                    ...additionalFieldValues,
                                    required: !additionalFieldValues.required,
                                  });
                                }}
                                handleDiameter={10}
                                width={28}
                                height={16}
                                checkedIcon={false}
                                uncheckedIcon={false}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col md="12">
                            <FormGroup className="select2-container">
                              <Label>
                                {t("Skills") + " *"}
                              </Label>
                              <Select
                                id="skills"
                                name="skills"
                                noOptionsMessage={() => t("No Options")}
                                ref={register}
                                classNamePrefix="select2-selection"
                                className={setClassName(
                                  errors.skills,
                                  "select2-selection"
                                )}
                                placeholder={t("Choose") + "..."}
                                options={skills}
                                getOptionLabel={(skill) => skill.name}
                                getOptionValue={(skill) => skill.id}
                                onChange={(skills) => {
                                  setAdditionalFieldValues({
                                    ...additionalFieldValues,
                                    skills: skills?.map((skill) => skill.id) ?? [],
                                  });
                                }}
                                isMulti
                                isClearable
                                value={skills.filter(skill => additionalFieldValues.skills.includes(skill.id))}

                              />
                              <div className="invalid-feedback d-block">
                                {t(errors.skills)}
                              </div>
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col md="12">
                            <h4 style={{ fontSize: 16, color: '#BBC4E0' }}>{t("Identification Key")} (Workflow)</h4>
                            <FormGroup className="select2-container">
                              <Label>{t('ID')} *</Label>
                              <input
                                name='key'
                                ref={register}
                                className={setClassName(errors.key, 'form-control')}
                                placeholder={t('Ex: ValorApr')}
                                onChange={handleChangeKeyValue}
                                value={additionalFieldValues.key ?? ''}
                              />
                              <div className='invalid-feedback d-block'>
                                {t(errors.key)}
                              </div>
                              <div className='d-block'>
                                <small>{t("The ID is used to identify the field in the database")}</small>
                              </div>
                            </FormGroup>
                          </Col>
                        </Row>
                      </Col>
                      <Col md="6">
                        <span>{t("Preview/field view")}</span>
                        {additionalFieldValues.type && (
                          <AdditionalFieldPreview
                            name={additionalFieldValues.name}
                            required={additionalFieldValues.required}
                            type={additionalFieldValues.type}
                            configuration={additionalFieldValues.configuration}
                            placeholder={additionalFieldValues.placeholder}
                          />
                        )}
                      </Col>
                    </Row>
                    <Link to="/additional-fields">
                      <Button
                        type="button"
                        color="primary"
                        outline
                        onClick={() => dispatch(resetAdditionalFields())}
                      >
                        {t("Cancel")}
                      </Button>
                    </Link>
                    <Button
                      type="submit"
                      color="primary"
                      className="ml-2 waves-effect waves-light"
                      style={{
                        minWidth: "100px",
                      }}
                      >
                        {loadingAdditionalField ? (
                          <Spinner
                            size="sm"
                            animation="border"
                            style={{
                              height: "19px",
                              width: "19px",
                            }}
                          />
                        ) : (
                          t("Save")
                        )}
                    </Button>
                        {/*
                    */}
                  </form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withTranslation()(AdditionalField);
